'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';

interface NewsletterPopupProps {
  open: boolean;
  onClose: () => void;
  onSubscribed?: () => void;
}

export default function NewsletterPopup({ open, onClose, onSubscribed }: NewsletterPopupProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setEmail('');
      setStatus('idle');
      setMessage('');
    }
  }, [open]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('loading');

    const res = await fetch('/api/newsletter/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await res.json();

    if (res.ok) {
      setStatus('success');
      setMessage(data.message || "You're subscribed!");
      setEmail('');
      onSubscribed?.();
    } else {
      setStatus('error');
      setMessage(data.error || 'Something went wrong.');
    }
  }

  return (
    <div className="nl-popup-overlay" onClick={onClose}>
      <div className="nl-popup-card" onClick={(e) => e.stopPropagation()}>
        <button className="nl-popup-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
        <div className="nl-popup-icon">
          <Image src="/newsletter-art.png" alt="The Daily Marquee" width={400} height={225} style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
        </div>
        <h2 className="nl-popup-heading">THE DAILY MARQUEE</h2>
        <p className="nl-popup-desc">
          The biggest entertainment stories delivered to your inbox every morning.
        </p>

        {status === 'success' ? (
          <p className="nl-popup-success">{message}</p>
        ) : (
          <form onSubmit={handleSubmit} className="nl-popup-form">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="nl-popup-input"
            />
            <button type="submit" disabled={status === 'loading'} className="nl-popup-submit">
              {status === 'loading' ? 'SUBSCRIBING...' : 'SUBSCRIBE'}
            </button>
            {status === 'error' && (
              <p className="nl-popup-error">{message}</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
